import React, { useState, useEffect, useRef } from 'react';
import { PostRequest, GetRequest } from 'components/common/index.js';
import { ContentPopup } from 'components/ContentPopup.jsx';
import QuoteBuilderForm from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderForm.jsx';

/**
 * Manages printing machines for a carton material in widget configuration.
 * Starts expanded and fetches machines on mount.
 *
 * @param {number|string} materialId - The carton material ID to manage machines for
 * @param {function} onError - Callback to set error message in parent
 */
const PrintingMachinesSection = ({ materialId, onError }) => {
  const [machines, setMachines] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [showMachinePopup, setShowMachinePopup] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreateMachinePopup, setShowCreateMachinePopup] = useState(false);
  const [formConfig, setFormConfig] = useState(null);

  const machinePopupRef = useRef();
  const createMachinePopupRef = useRef();

  const fetchFormConfig = () => {
    GetRequest({
      url: '/quote-builder/config/settings/user_unit_prices/printing_machine',
      useCache: true,
      callback: (data) => {
        setFormConfig(data);
      }
    });
  };

  const fetchMachines = () => {
    GetRequest({
      url: `/quote-builder/user_unit_prices/${materialId}/widget-printing-machines`,
      callback: (data) => {
        setMachines(data.printing_machines || []);
      }
    });
  };

  useEffect(() => {
    fetchMachines();
    fetchFormConfig();
  }, [materialId]);

  const updateMachines = (userUnitPriceIds) => {
    setSaving(true);
    PostRequest({
      url: `/quote-builder/user_unit_prices/${materialId}/update-widget-printing-machines`,
      data: { printing_machine_user_unit_price_ids: userUnitPriceIds },
      callback: () => {
        setSaving(false);
        fetchMachines();
      },
      400: () => {
        setSaving(false);
        onError(t('quote_builder_widget.failed_to_update_material'));
      },
      500: () => {
        setSaving(false);
        onError(t('quote_builder_widget.server_error'));
      }
    });
  };

  const addMachine = (userUnitPriceId) => {
    const currentlyAssigned = machines.filter((machine) => machine.assigned).map((machine) => String(machine.id));
    const updatedIds = [...currentlyAssigned, String(userUnitPriceId)];
    updateMachines(updatedIds);
    setShowMachinePopup(false);
    if (machinePopupRef.current) {
      machinePopupRef.current.style.display = 'none';
    }
  };

  const removeMachine = (userUnitPriceId) => {
    const updatedIds = machines
      .filter((machine) => machine.assigned && String(machine.id) !== String(userUnitPriceId))
      .map((machine) => String(machine.id));
    updateMachines(updatedIds);
  };

  const toggleExpanded = () => {
    setExpanded((previous) => !previous);
  };

  const handleNewMachineCreated = () => {
    setShowCreateMachinePopup(false);
    if (createMachinePopupRef.current) {
      createMachinePopupRef.current.style.display = 'none';
    }
    fetchMachines();
  };

  const assignedMachines = machines.filter((machine) => machine.assigned);
  const unassignedMachines = machines.filter((machine) => !machine.assigned);

  return (
    <div style={{
      margin: '0',
      backgroundColor: '#fdfdfe',
      overflow: 'hidden'
    }}>
      <button
        type="button"
        className="btn btn-sm"
        style={{
          color: '#495057',
          padding: '6px 12px',
          fontSize: '12px',
          width: '100%',
          textAlign: 'left',
          backgroundColor: '#f1f3f5',
          borderRadius: '0',
          borderBottom: expanded ? '1px solid #dee2e6' : 'none'
        }}
        onClick={toggleExpanded}
      >
        <i className={`fa fa-caret-${expanded ? 'down' : 'right'}`} aria-hidden="true"></i>
        {' '}{t('quote_builder.printing_machine_plural')}
        <span style={{ marginLeft: '5px', color: assignedMachines.length > 0 ? '#28a745' : '#999' }}>
          ({assignedMachines.length})
        </span>
      </button>

      {expanded && (
        <div style={{ padding: '8px 12px' }}>
          {assignedMachines.length === 0 ? (
            <div style={{ color: '#666', fontStyle: 'italic', fontSize: '13px', marginBottom: '5px' }}>
              {t('quote_builder_widget.no_printing_machines_assigned')}
            </div>
          ) : (
            assignedMachines.map((machine) => (
              <div key={machine.id} style={{
                padding: '4px 8px',
                backgroundColor: '#e9ecef',
                borderRadius: '3px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '13px'
              }}>
                <span>{machine.description}</span>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ color: '#dc3545', padding: '0 6px', fontSize: '11px' }}
                  onClick={() => removeMachine(machine.id)}
                  disabled={saving}
                >
                  <i className="fa fa-times" aria-hidden="true"></i>
                </button>
              </div>
            ))
          )}

          <button
            type="button"
            className="btn btn-sm"
            style={{ color: '#0d6efd', padding: '2px 8px', fontSize: '12px', marginTop: '4px' }}
            onClick={() => setShowMachinePopup(true)}
          >
            <i className="fa fa-plus" aria-hidden="true"></i>
            {' '}{t('quote_builder.new_printing_machine')}
          </button>

          {showMachinePopup && (
            <ContentPopup
              myRef={machinePopupRef}
              autoOpen={true}
              content={
                <div style={{ padding: '20px', minWidth: '400px' }}>
                  <h4>{t('quote_builder_widget.select_printing_machine')}</h4>
                  <hr />
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {unassignedMachines.map((machine) => (
                      <div
                        key={machine.id}
                        onClick={() => addMachine(machine.id)}
                        style={{
                          padding: '10px 15px',
                          borderRadius: '4px',
                          marginBottom: '5px',
                          cursor: 'pointer',
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #dee2e6'
                        }}
                      >
                        <div><strong>{machine.description}</strong></div>
                        {machine.summary && (
                          <small style={{ color: '#666' }} dangerouslySetInnerHTML={{ __html: machine.summary }} />
                        )}
                      </div>
                    ))}
                  </div>
                  <hr />
                  <button
                    type="button"
                    className="btn simple-btn"
                    onClick={() => {
                      setShowMachinePopup(false);
                      if (machinePopupRef.current) {
                        machinePopupRef.current.style.display = 'none';
                      }
                      setShowCreateMachinePopup(true);
                    }}
                  >
                    <i className="fa fa-plus" aria-hidden="true"></i>
                    {' '}{t('quote_builder.new_printing_machine')}
                  </button>
                </div>
              }
              cancelButton={false}
              beforeClose={() => setShowMachinePopup(false)}
            />
          )}

          {showCreateMachinePopup && formConfig && (
            <ContentPopup
              myRef={createMachinePopupRef}
              autoOpen={true}
              content={
                <QuoteBuilderForm
                  config={formConfig}
                  showingState="new"
                  afterSave={handleNewMachineCreated}
                  afterCancel={handleNewMachineCreated}
                />
              }
              cancelButton={false}
              beforeClose={handleNewMachineCreated}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PrintingMachinesSection;
